import React, { memo, useCallback, useRef, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { UploadDocumentIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import {
  MULTI_FILE_DROPZONE_DEFAULT_BROWSE_LABEL,
  MULTI_FILE_DROPZONE_DEFAULT_TITLE,
} from './MultiFileDropzone.constants';
import {
  dropzoneActiveStyles,
  dropzoneBrowseRowStyles,
  dropzoneTitleStyles,
  dropzoneUploadTileStyles,
  dropzoneWrapperStyles,
  fileInputHiddenStyle,
} from './MultiFileDropzone.styles';

// Shared multi-file dropzone shell (hidden multi-input + drag/drop wrapper + upload tile + title +
// browse button). Owns the drag state, file emission, and the click-to-browse affordance so callers
// only supply copy, an accept filter, an onFilesSelected sink, and any inline content (children).
const MultiFileDropzone = ({
  onFilesSelected,
  accept = null,
  title = MULTI_FILE_DROPZONE_DEFAULT_TITLE,
  hint = null,
  browseLabel = MULTI_FILE_DROPZONE_DEFAULT_BROWSE_LABEL,
  dropzoneTestId = null,
  fileInputTestId = null,
  browseButtonTestId = null,
  children = null,
}) => {
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const onBrowseClick = useCallback(() => inputRef.current?.click(), []);

  const emitFiles = useCallback(files => onFilesSelected(Array.from(files)), [onFilesSelected]);

  const onFileInputChange = useCallback(
    event => {
      emitFiles(event.target.files);
      event.target.value = '';
    },
    [emitFiles],
  );

  const onDragOver = useCallback(event => {
    event.preventDefault();
    setIsDragActive(true);
  }, []);

  const onDragLeave = useCallback(() => setIsDragActive(false), []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      setIsDragActive(false);
      emitFiles(event.dataTransfer.files);
    },
    [emitFiles],
  );

  // Keep a click on the browse button (and any inline children) from bubbling to the dropzone's own
  // click-to-browse handler, which would open the file picker a second time in the same tick.
  const onStopPropagation = useCallback(event => event.stopPropagation(), []);

  const dropzoneSx = isDragActive
    ? { ...dropzoneWrapperStyles, ...dropzoneActiveStyles }
    : dropzoneWrapperStyles;

  return (
    <>
      <input
        multiple
        accept={accept}
        data-testid={fileInputTestId}
        ref={inputRef}
        style={fileInputHiddenStyle}
        type="file"
        onChange={onFileInputChange}
      />
      <Box
        data-testid={dropzoneTestId}
        sx={dropzoneSx}
        onClick={onBrowseClick}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <Box sx={dropzoneUploadTileStyles}>
          <UploadDocumentIcon size={28} />
        </Box>
        <Typography color="primary" sx={dropzoneTitleStyles} variant="bold16">
          {title}
        </Typography>
        {hint}
        {children}
        <Box sx={dropzoneBrowseRowStyles} onClick={onStopPropagation}>
          <Button data-testid={browseButtonTestId} variant="contained" onClick={onBrowseClick}>
            {browseLabel}
          </Button>
        </Box>
      </Box>
    </>
  );
};

MultiFileDropzone.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  accept: PropTypes.string,
  title: PropTypes.string,
  hint: PropTypes.node,
  browseLabel: PropTypes.string,
  dropzoneTestId: PropTypes.string,
  fileInputTestId: PropTypes.string,
  browseButtonTestId: PropTypes.string,
  children: PropTypes.node,
};

export default memo(MultiFileDropzone);
